/**
 * Human-readable rendering for structured tracker-apply {@link ImportWarning}s.
 *
 * The tracker apply endpoint returns warnings as a structured union (unlike the
 * Excel path, which stringifies on the backend). This formatter turns each
 * variant into a single user-facing line, mirroring the backend
 * `format_import_warning` wording so both import paths read identically.
 */

import type { ImportWarning } from './types';

/**
 * Render one warning as a single line. The switch is exhaustive over the
 * generated union; the `default` is a forward-compatibility guard so a newly
 * added backend warning kind (before this file is updated) is surfaced verbatim
 * rather than silently dropped.
 */
export function formatTrackerWarning(w: ImportWarning): string {
  switch (w.kind) {
    case 'unresolved_predecessor':
      return `Predecessor '${w.predecessor_source_id}' for '${w.successor_source_id}' is not yet imported; the dependency will link automatically once it is.`;
    case 'unparseable_field':
      return `Couldn't parse field '${w.field}' on '${w.source_id}' (value: '${w.raw_value}'); it was left unset.`;
    case 'unknown_statuses':
      return `Statuses not mapped to any workflow column (departure '${w.departure_column}'): ${w.statuses.join(', ')}.`;
    case 'departure_column_not_found':
      return `Departure column '${w.departure_column}' wasn't found in the workflow; available columns: ${w.available_columns.join(', ')}.`;
    case 'empty_throughput':
      return "This ingestion produced no throughput data; the team's existing throughput was kept.";
    case 'feature_name_conflict':
      return `Feature '${w.name}' already belongs to '${w.existing_source_id}'; the incoming '${w.incoming_source_id}' was skipped to avoid stealing it.`;
    case 'unresolved_feature_parent': {
      // Mirrors the backend's singular/plural branch verbatim — see
      // `format_import_warning` in crates/api/src/api/sheet/handlers.rs.
      const subject = w.deliverable_source_ids.length === 1 ? 'that deliverable' : 'those deliverables';
      return `Feature parent '${w.parent_source_id}' for ${w.deliverable_source_ids.join(', ')} is not yet imported; ${subject} will be grouped automatically once it is.`;
    }
    case 'ambiguous_feature_parent':
      return `'${w.deliverable_source_id}' names several feature parents (${w.candidate_source_ids.join(', ')}); a deliverable belongs to exactly one, so the first was used.`;
    case 'shared_dataset_in_simulation':
      return `Dataset '${w.dataset_name}' is shared by teams ${w.team_names.join(', ')} in this simulation — their queries return identical work-item counts, so the per-team split won't happen.`;
    default:
      return JSON.stringify(w);
  }
}
